/**
 * Het hartje op een productkaart of productpagina.
 *
 * Rendert altijd de LEGE staat vanuit Liquid en zet de gevulde staat pas in de
 * browser: Shopify levert collectiepagina's uit één cache aan alle bezoekers,
 * dus staat in de HTML zou naar andere mensen lekken.
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { wishlist, WISHLIST_CHANGE } from '@theme/wishlist-store';
import { showWishlistHint } from '@theme/wishlist-hint';

class WishlistButton extends Component {
  requiredRefs = ['button'];

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#render();
    document.addEventListener(WISHLIST_CHANGE, () => this.#render(), { signal });
    this.refs.button.addEventListener('click', this.#onClick, { signal });

    // Op de productpagina wisselt de gekozen variant. Zonder dit zou het hartje
    // de variant bewaren die bij het laden geselecteerd was.
    if (this.dataset.trackVariant) {
      document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#controller?.abort();
  }

  #controller = null;

  #onClick = (event) => {
    event.preventDefault();
    event.stopPropagation(); // de hele kaart is een link; het hartje niet

    const variantId = Number(this.dataset.variantId);
    const before = wishlist.items().find((item) => item.id === variantId);

    const { added } = wishlist.toggle({
      id: variantId,
      handle: this.dataset.handle,
      price: Number(this.dataset.price),
    });

    showWishlistHint({
      anchor: this.refs.button,
      title: this.dataset.title ?? '',
      added,
      item: before ? { ...before } : null,
      focus: event.detail === 0,
    });
  };

  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant?.id) return;

    this.dataset.variantId = String(variant.id);
    if (variant.price != null) this.dataset.price = String(variant.price);
    this.#render();
  };

  #render() {
    const on = wishlist.has(this.dataset.variantId);
    const button = this.refs.button;
    button.setAttribute('aria-pressed', String(on));
    button.setAttribute('aria-label', on ? this.dataset.labelRemove ?? '' : this.dataset.labelAdd ?? '');
    button.classList.toggle('wishlist-button--on', on);
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
